import React from 'react';

import { showToast } from '@opendoor/bricks/complex/Toast';
import { Button, Divider, Flex, Text } from '@opendoor/bricks/core';

import {
  OdProtosListingsData_LwcProduct_State as LWC_PRODUCT_STATE,
  OdProtosListingsData_NoteType as NOTE_TYPE,
  OdProtosAgentsData_AgentLead_State,
} from '../../__generated__/athenaGlobalTypes';
import { useAcceptListingLead } from '../../graphql/athena/mutations/AcceptListingLead';
import { useRejectListingLead } from '../../graphql/athena/mutations/RejectListingLead';
import { useAthenaSessionQuery } from '../../graphql/athena/queries/Session';
import { getHomeAssessmentData } from '../../graphql/athena/useExteriorAssessment';
import { SingleLead } from '../../helpers/getAllLeadData';
import { CustomerInfo } from '../../routes/LwcDashboard/CustomerInfo';
import { DidNotConvertForm } from '../../routes/LwcDashboard/DidNotConvertForm';
import { InContractForm } from '../../routes/LwcDashboard/InContractForm';
import { ListingAgreementSignedForm } from '../../routes/LwcDashboard/ListingAgreementSignedForm';
import { ListingInfo } from '../../routes/LwcDashboard/ListingInfo';
import { ListingPresentationCompletedForm } from '../../routes/LwcDashboard/ListingPresentationCompletedForm';
import { ListingPresentationScheduledForm } from '../../routes/LwcDashboard/ListingPresentationScheduledForm';
import { OfferInfo } from '../../routes/LwcDashboard/OfferInfo';
import { ReassignLeadForm } from '../../routes/LwcDashboard/ReassignLeadForm';
import { SetNotesForm } from '../../routes/LwcDashboard/SetNotesForm';
import { SoldForm } from '../../routes/LwcDashboard/SoldForm';
import { AttemptedFirstContactForm } from './AttemptedFirstContactForm';
import { ListingActiveForm } from './ListingActiveForm';
import { ListingFallbackForm } from './ListingFallbackForm';
import { SupportRequestForm } from './SupportRequestForm';
import Timeline from './Timeline';
import { getAllowedStateTransitionsForSingleLead, getDisplayStateFromSingleLead } from './utils';

type LeadDetailsPageProps = {
  leadDetails: SingleLead;
  brokerageView?: boolean;
};

/* Do not show the state control UI for these states */
const STATE_CONTROL_UI_HIDDEN_STATES = [LWC_PRODUCT_STATE.UNKNOWN, LWC_PRODUCT_STATE.DELETED];

export const LeadDetailsPage: React.FC<LeadDetailsPageProps> = ({ leadDetails, brokerageView }) => {
  const lwcProductData = leadDetails.lwcProductData;
  const {
    lwcProductId,
    lwcProductState,
    listingId,
    listingState,
    soldPrice,
    grossCommission,
    pendingAssignmentAcceptance,
    agentLeadState,
    agentLeadId,
  } = lwcProductData;

  const pendingBrokerageAcceptance =
    agentLeadState === OdProtosAgentsData_AgentLead_State.BROKERAGE_PROPOSED;
  const displayState = getDisplayStateFromSingleLead(leadDetails);
  const showStateManipulationUI =
    !STATE_CONTROL_UI_HIDDEN_STATES.includes(lwcProductState) && !pendingAssignmentAcceptance;

  const allowedStateTransitions = getAllowedStateTransitionsForSingleLead(leadDetails);

  const { acceptLead } = useAcceptListingLead();
  const { rejectLead } = useRejectListingLead();
  const { customerUuid } = useAthenaSessionQuery();

  const homeAssessmentData = getHomeAssessmentData(
    leadDetails.homeAssessmentData?.currentInspectionTime,
    leadDetails.offerData?.initialInspectionAt,
  );

  return (
    <Flex background={'smokywhite'} flexDirection="column">
      {/* Customer Information Section */}
      <Flex flexDirection="column" fontSize={'s3'} gridGap={3}>
        {pendingAssignmentAcceptance && customerUuid && (
          <Flex gridGap="inherit">
            <Button
              analyticsName="accept-lead"
              aria-label=""
              width="100%"
              type="button"
              onClick={() =>
                acceptLead(customerUuid, leadDetails.lwcProductData.agentLeadId).then(() => {
                  showToast('info', `Successfully accepted lead`, 'lwc-dashboard-toast}', {
                    toastOptions: { autoClose: 3000 },
                  });
                })
              }
              backgroundColor={'green50'}
              sx={{
                ':hover': {
                  backgroundColor: 'lightgrey',
                  color: 'green50',
                },
              }}
            >
              {agentLeadState === OdProtosAgentsData_AgentLead_State.BROKERAGE_PROPOSED
                ? 'Brokerage Accept'
                : 'Accept'}
            </Button>
            <Button
              type="button"
              onClick={() => rejectLead(customerUuid, leadDetails.lwcProductData.agentLeadId)}
              analyticsName="reject-lead"
              aria-label=""
              width="100%"
              backgroundColor={'red50'}
              sx={{
                ':hover': {
                  backgroundColor: 'lightgrey',
                  color: 'red50',
                },
              }}
            >
              {pendingBrokerageAcceptance ? 'Brokerage Reject' : 'Reject'}
            </Button>
          </Flex>
        )}
        {/* Customer contact info + address data */}
        <CustomerInfo customerData={leadDetails?.customerData} />
        {/* Opendoor specific details including estimated listing range, finalized offer, and experience partner info */}
        <Divider />
        <OfferInfo
          offerData={leadDetails?.offerData}
          lwcProductId={lwcProductId}
          experiencePartner={leadDetails?.experiencePartner}
          lwcProductData={leadDetails?.lwcProductData}
          homeAssessmentData={homeAssessmentData}
        />

        <Divider />

        {/* Listing state  */}
        <ListingInfo
          displayState={displayState}
          listingPrice={leadDetails?.lwcProductData?.listingPrice ?? 0}
          soldPrice={leadDetails?.lwcProductData?.soldPrice ?? 0}
          grossCommission={leadDetails?.lwcProductData?.grossCommission ?? 0}
        >
          {showStateManipulationUI && (
            <Flex flexDirection="column" gridGap="3">
              {allowedStateTransitions.FirstContactAttempted && (
                <AttemptedFirstContactForm lwcProductId={lwcProductId} />
              )}
              {allowedStateTransitions.ListingPresentationScheduled && (
                <ListingPresentationScheduledForm lwcProductId={lwcProductId} />
              )}
              {allowedStateTransitions.ListingPresentationComplete && (
                <ListingPresentationCompletedForm lwcProductId={lwcProductId} />
              )}
              {allowedStateTransitions.ListingAgreementSigned && (
                <ListingAgreementSignedForm lwcProductId={lwcProductId} />
              )}
              {allowedStateTransitions.Listed && <ListingActiveForm listingId={listingId} />}
              {allowedStateTransitions.InContract && (
                <InContractForm
                  listingId={listingId}
                  soldPrice={soldPrice}
                  grossCommission={grossCommission}
                />
              )}
              {allowedStateTransitions.Close && <SoldForm listingId={listingId} />}
              <ListingFallbackForm listingId={listingId} listingState={listingState} />
              {/* Note specific to the agent interactions with the customer. */}
              <SetNotesForm
                lwcProductId={lwcProductId}
                noteType={NOTE_TYPE.AGENT_NOTE}
                currentNote={leadDetails.lwcProductData.agentNote ?? ''}
              />
              {allowedStateTransitions.DidNotConvert && (
                <DidNotConvertForm lwcProductId={lwcProductId} />
              )}
              <SupportRequestForm lwcProductId={lwcProductId} />
            </Flex>
          )}
        </ListingInfo>
        {customerUuid && !pendingBrokerageAcceptance && brokerageView && (
          <>
            <Divider />
            <Text fontWeight="semibold">Agent assignment</Text>
            <ReassignLeadForm agentLeadId={agentLeadId} agentCustomerUuid={customerUuid} />
          </>
        )}
        <Divider />
        <Text fontWeight="semibold">Timeline</Text>
        <Timeline leadDetails={leadDetails} />
      </Flex>
    </Flex>
  );
};
